import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useDealerId } from "../stores/hooks/useDealerId";
import { useNationalAccountId } from "../stores/hooks/useNationalAccountId";
import { useSearchForm } from "../stores/hooks/useSearchForm";
import { useSecondaryManufacturersProp } from "../stores/hooks/useSecondaryManufacturersProp";
import { ComboBoxOption } from "../ui/ComboBox/useComboBox";
import { useGetVehicleYears } from "./useGetVehicleYears";

type Option = ComboBoxOption<number, null>;

export const getVehicleManufacturersEndpoint =
  "/retailSiteWebService/vehicle/getVehicleManufacturers.php";

type Options = {
  forcePrimary?: boolean;
};

export const useGetVehicleManufacturers = ({
  forcePrimary = false,
}: Options = {}): ModifiedSWRResponse<
  FetcherResult<VehicleManufacturersPayload>,
  Option[]
> & {
  nationalAccountId: string;
  vehicleManufacturerId?: number;
  vehicleYearId?: number;
} => {
  const [{ vehicleManufacturerName }] = useSearchForm();
  const { vehicleYearId } = useGetVehicleYears();
  const dealerId = useDealerId();
  const nationalAccountId = useNationalAccountId();
  const secondaryManufacturersProp =
    useSecondaryManufacturersProp() && !forcePrimary;

  const { data, ...res } = useSwrGet<VehicleManufacturersPayload>(
    vehicleYearId ? getVehicleManufacturersEndpoint : null,
    {
      dealerId,
      nationalAccountId,
      vehicleYearId: String(vehicleYearId || ""),
    }
  );

  if (!data) {
    return {
      ...res,
      data: undefined,
      nationalAccountId,
    };
  }

  const primaryVehicleManufacturers: Option[] =
    data.primaryVehicleManufacturers.map(
      ({ vehicleManufacturerId, vehicleManufacturerName }) => ({
        data: null,
        label: vehicleManufacturerName,
        value: vehicleManufacturerId,
      })
    );

  const secondaryVehicleManufacturers: Option[] = secondaryManufacturersProp
    ? data.secondaryVehicleManufacturers.map<Option>(
        ({ vehicleManufacturerId, vehicleManufacturerName }) => ({
          data: null,
          label: vehicleManufacturerName,
          value: vehicleManufacturerId,
        })
      )
    : [];

  const options = [
    ...primaryVehicleManufacturers,
    ...secondaryVehicleManufacturers,
  ];

  const vehicleManufacturerId = options.find(
    ({ label }) =>
      label?.toLowerCase() === vehicleManufacturerName?.toLowerCase()
  )?.value;

  return {
    ...res,
    data: options,
    nationalAccountId,
    vehicleManufacturerId,
    vehicleYearId,
  };
};

export type VehicleManufacturer = {
  vehicleManufacturerId: number;
  vehicleManufacturerImage: string;
  vehicleManufacturerName: string;
  vehicleManufacturerSortOrder: string;
};

export type VehicleManufacturersPayload = {
  primaryVehicleManufacturers: VehicleManufacturer[];
  secondaryVehicleManufacturers: VehicleManufacturer[];
};

/**
 * @see docs/worklog/TW-914-standalone-accessories-site-changes/rfc.md
 * @see docs/worklog/TW-914-standalone-accessories-site-changes/tfc.md
 */
